import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { useEisRuleConfiguration } from '../_data_sources/useEisRuleConfiguration';
import useSaveEisRuleConfiguration from '../_data_sources/useSaveEisRuleConfiguration';
import type {
  EisConfigScoringTemplate,
  EisNotificationRecipient,
  EisRuleConfigurationResponse,
  EisRuleFrequency,
  EisRuleNotifications,
  EisRuleThreshold,
} from '../_types';

interface EisRuleConfigContextValue {
  configuration: EisRuleConfigurationResponse | null;
  organizationId: string;
  isLoading: boolean;
  isSaving: boolean;

  scoringTemplates: EisConfigScoringTemplate[];
  setScoringTemplates: (templates: EisConfigScoringTemplate[]) => void;

  scoreThreshold: number | null;
  setScoreThreshold: (value: number | null) => void;
  windowDays: EisRuleThreshold['windowDays'];
  setWindowDays: (value: EisRuleThreshold['windowDays']) => void;

  notifySupervisors: boolean;
  setNotifySupervisors: (value: boolean) => void;
  notifyAdditional: boolean;
  setNotifyAdditional: (value: boolean) => void;
  additionalRecipients: EisNotificationRecipient[];
  setAdditionalRecipients: (value: EisNotificationRecipient[]) => void;

  ruleType: 'Frequency' | 'Scoring';
  incidentCount: number | null;
  setIncidentCount: (value: number | null) => void;
  frequencyWindowDays: number | null;
  setFrequencyWindowDays: (value: number | null) => void;

  isPublishing: boolean;
  handleSaveDraft: () => void;
  handlePublish: () => Promise<void>;
  handleBack: () => Promise<void>;
}

const EisRuleConfigContext = createContext<EisRuleConfigContextValue | undefined>(undefined);

export const EisRuleConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { id } = useParams<{ id: string }>();
  const ruleId = id ? Number(id) : 0;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { currentOrganizationId } = useContext(AppContext);

  const { data: configuration, isLoading } = useEisRuleConfiguration(ruleId, {}, (err) => {
    console.error('Error fetching EIS rule configuration:', err);
  });

  const { mutate: saveConfiguration, isLoading: isSaving } = useSaveEisRuleConfiguration();

  const [scoringTemplates, setScoringTemplates] = useState<EisConfigScoringTemplate[]>([]);
  const [scoreThreshold, setScoreThreshold] = useState<number | null>(null);
  const [windowDays, setWindowDays] = useState<EisRuleThreshold['windowDays']>(null);
  const [notifySupervisors, setNotifySupervisors] = useState(false);
  const [notifyAdditional, setNotifyAdditional] = useState(false);
  const [additionalRecipients, setAdditionalRecipients] = useState<EisNotificationRecipient[]>([]);
  const [ruleType, setRuleType] = useState<'Frequency' | 'Scoring'>('Scoring');
  const [incidentCount, setIncidentCount] = useState<number | null>(null);
  const [frequencyWindowDays, setFrequencyWindowDays] = useState<number | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    if (!configuration) return;

    const threshold =
      configuration.threshold && 'scoreThreshold' in configuration.threshold
        ? (configuration.threshold as EisRuleThreshold)
        : null;
    const notifications =
      configuration.notifications && 'notifySupervisors' in configuration.notifications
        ? (configuration.notifications as EisRuleNotifications)
        : null;

    setScoringTemplates(configuration.scoring?.templates ?? []);
    setScoreThreshold(threshold?.scoreThreshold ?? null);
    setWindowDays(threshold?.windowDays ?? null);
    setNotifySupervisors(notifications?.notifySupervisors ?? false);
    setNotifyAdditional(notifications?.notifyAdditional ?? false);
    setAdditionalRecipients(notifications?.additionalRecipients ?? []);

    const resolvedRuleType = configuration.rule.ruleType ?? 'Scoring';
    setRuleType(resolvedRuleType);
    if (resolvedRuleType === 'Frequency') {
      const freq =
        configuration.frequency && 'incidentCount' in configuration.frequency
          ? (configuration.frequency as EisRuleFrequency)
          : null;
      setIncidentCount(freq?.incidentCount ?? null);
      setFrequencyWindowDays(freq?.windowDays ?? null);
    }
  }, [configuration]);

  const handlePublish = async () => {
    if (!configuration) return;
    try {
      await confirm({
        title: t('performanceReporting.eis.configuration.publishConfirm.title'),
        description: t('performanceReporting.eis.configuration.publishConfirm.description'),
        confirmationText: t('performanceReporting.eis.configuration.publishConfirm.confirm'),
        cancellationText: t('performanceReporting.eis.configuration.publishConfirm.cancel'),
        confirmationButtonProps: { variant: 'contained' },
        cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
      });
    } catch {
      // User cancelled — do nothing
      return;
    }
    setIsPublishing(true);
    saveConfiguration(
      {
        ruleId: configuration.rule.id,
        data: {
          rule: { ...configuration.rule, versionStatus: 'Active' },
          scoring: { templates: scoringTemplates },
          threshold: { scoreThreshold, windowDays },
          notifications: { notifySupervisors, notifyAdditional, additionalRecipients },
          ...(ruleType === 'Frequency' ? { frequency: { incidentCount, windowDays: frequencyWindowDays } } : {}),
        },
      },
      {
        onSuccess: () => {
          setIsPublishing(false);
          enqueueSnackbar(t('performanceReporting.eis.configuration.publishSuccess'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
          navigate('/admin/performance-reporting/early-intervention-system');
        },
        onError: () => {
          setIsPublishing(false);
          enqueueSnackbar(t('performanceReporting.eis.configuration.publishError'), {
            variant: 'error',
            autoHideDuration: 5000,
          });
        },
      },
    );
  };

  const handleBack = async () => {
    try {
      await confirm({
        title: t('performanceReporting.eis.configuration.discardChanges.title'),
        description: t('performanceReporting.eis.configuration.discardChanges.description'),
        confirmationText: t('performanceReporting.eis.configuration.discardChanges.confirm'),
        cancellationText: t('performanceReporting.eis.configuration.discardChanges.cancel'),
        confirmationButtonProps: { variant: 'contained' },
        cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
      });
      navigate('..');
    } catch {
      // User clicked "Keep Editing" — do nothing
    }
  };

  const handleSaveDraft = () => {
    if (!configuration) return;

    saveConfiguration(
      {
        ruleId: configuration.rule.id,
        data: {
          rule: { ...configuration.rule, versionStatus: 'Draft' },
          scoring: { templates: scoringTemplates },
          threshold: { scoreThreshold, windowDays },
          notifications: { notifySupervisors, notifyAdditional, additionalRecipients },
          ...(ruleType === 'Frequency' ? { frequency: { incidentCount, windowDays: frequencyWindowDays } } : {}),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('performanceReporting.eis.configuration.saveDraftSuccess'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
          navigate('/admin/performance-reporting/early-intervention-system');
        },
        onError: () => {
          enqueueSnackbar(t('performanceReporting.eis.configuration.saveDraftError'), {
            variant: 'error',
            autoHideDuration: 5000,
          });
        },
      },
    );
  };

  const contextValue: EisRuleConfigContextValue = {
    configuration: configuration ?? null,
    organizationId: currentOrganizationId || '',
    isLoading,
    isSaving,

    scoringTemplates,
    setScoringTemplates,

    scoreThreshold,
    setScoreThreshold,
    windowDays,
    setWindowDays,

    notifySupervisors,
    setNotifySupervisors,
    notifyAdditional,
    setNotifyAdditional,
    additionalRecipients,
    setAdditionalRecipients,

    ruleType,
    incidentCount,
    setIncidentCount,
    frequencyWindowDays,
    setFrequencyWindowDays,

    isPublishing,
    handleSaveDraft,
    handlePublish,
    handleBack,
  };

  return <EisRuleConfigContext.Provider value={contextValue}>{children}</EisRuleConfigContext.Provider>;
};

export const useEisRuleConfigContext = () => {
  const context = useContext(EisRuleConfigContext);
  if (!context) {
    throw new Error('useEisRuleConfigContext must be used within an EisRuleConfigProvider');
  }
  return context;
};
